import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  Avatar,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation } from "@apollo/client";
import { REQUEST_AI_EVIDENCE_ACCESS } from "../../../apis/integrated-evidence/aiEvidenceApis";
import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog";
import ConfirmSnackbar from "../../../components/Snackbar/ConfirmSnackbar";
import RequestReviewDialog from "../../../components/RequestReviewDialog/RequestReviewDialog";
import {
  initials,
  splitIntegrations,
} from "../../../utils/aiEvidenceOwners";

const AIEvidenceLockedStrip = ({ checkId, owners = [] }) => {
  const theme = useTheme();
  const tc = theme.customColors;
  const info = tc.status.info;

  const multiple = owners.length > 1;

  // Distinct integrations across all owners — names the access being requested.
  const allIntegrations = Array.from(
    new Set(owners.flatMap((o) => splitIntegrations(o.integrations))),
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState(null);
  const [snackbarInfo, setSnackbarInfo] = useState(null);

  const [requestAccess, { loading: sending }] = useMutation(
    REQUEST_AI_EVIDENCE_ACCESS,
  );

  const openDialog = () => {
    // A lone owner has no checkbox to tick, so it is selected implicitly.
    // With several, start empty so the choice is deliberate.
    setSelected(
      multiple ? new Set() : new Set(owners.map((o) => o.employeeDataId)),
    );
    setError(null);
    setDialogOpen(true);
  };

  const toggleOwner = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = async () => {
    setError(null);
    try {
      const res = await requestAccess({
        variables: { checkId, ownerIds: Array.from(selected) },
      });
      const result = res?.data?.requestAIEvidenceAccess;
      if (result?.success) {
        const names = owners
          .filter((o) => selected.has(o.employeeDataId))
          .map((o) => o.name || o.email)
          .filter(Boolean);
        const recipients =
          names.length === 0
            ? "the data owners"
            : names.length === 1
              ? names[0]
              : names.length === 2
                ? `${names[0]} and ${names[1]}`
                : `${names[0]} and ${names.length - 1} others`;
        setDialogOpen(false);
        setSnackbarInfo({
          message: `Access request sent to ${recipients}`,
          vertical: "top",
          horizontal: "right",
          key: Date.now(),
        });
      } else {
        setError(result?.message || "Could not send the request.");
      }
    } catch (err) {
      setError("Could not send the request.");
    }
  };

  return (
    <>
      <Box sx={{ px: 1, pb: 1 }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            mx: 0.5,
            borderRadius: "12px",
            border: `1px dashed ${alpha(info.main, 0.3)}`,
            backgroundColor: alpha(info.main, 0.03),
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {!multiple && (
            <Avatar
              sx={{
                width: 26,
                height: 26,
                fontSize: "11px",
                fontWeight: 600,
                bgcolor: alpha(info.main, 0.14),
                color: info.text,
              }}
            >
              {initials(owners[0]?.name || owners[0]?.email)}
            </Avatar>
          )}

          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontSize: "13.5px", color: tc.text.strong }}>
              {multiple
                ? `Managed by ${owners.length} other data owners`
                : `Managed by ${owners[0]?.name || owners[0]?.email}`}
            </Typography>
            <Typography sx={{ fontSize: "12.5px", color: tc.text.secondary }}>
              {allIntegrations.length > 0
                ? `Request access to ${allIntegrations.join(", ")}`
                : "Request access to review them"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* The other way out: leave the evidence with its owner and ask
                them to review it themselves. */}
            <Button
              variant="outlined"
              disableElevation
              onClick={() => setReviewOpen(true)}
              sx={{
                fontWeight: 500,
                borderRadius: "8px",
                textTransform: "none",
                fontSize: "13.5px",
                px: 1.75,
                py: 0.75,
                whiteSpace: "nowrap",
                backgroundColor: tc.background.main,
                borderColor: tc.border.strong,
                color: tc.text.strong,
                "&:hover": {
                  borderColor: tc.text.secondary,
                  backgroundColor: tc.background.hover,
                },
              }}
            >
              Ask them to review
            </Button>

            <Button
              variant="contained"
              disableElevation
              onClick={openDialog}
              sx={{
                bgcolor: info.main,
                color: tc.button.text,
                fontWeight: 500,
                borderRadius: "8px",
                textTransform: "none",
                fontSize: "13.5px",
                px: 1.75,
                py: 0.75,
                boxShadow: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  bgcolor: info.text,
                  boxShadow: tc.shadow.filterChip,
                },
              }}
            >
              Request access
            </Button>
          </Box>
        </Box>
      </Box>

      <ConfirmDialog
        open={dialogOpen}
        onClose={() => !sending && setDialogOpen(false)}
        onConfirm={handleConfirm}
        loading={sending}
        confirmDisabled={selected.size === 0}
        heading="Request access"
        buttonText="Send request"
        note={error}
        content={
          <Box>
            <Typography
              sx={{ fontSize: "14px", color: tc.text.secondary, mb: multiple ? 2 : 0 }}
            >
              {multiple
                ? "Choose who to ask. We'll email them asking to share their integration with you."
                : `${owners[0]?.name || owners[0]?.email} will be emailed to grant you access to ${
                    allIntegrations.length > 0
                      ? allIntegrations.join(", ")
                      : "the relevant integration"
                  }.`}
            </Typography>

            {multiple && (
              <Box
                sx={{
                  border: `1px solid ${tc.border.subtle}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                {owners.map((owner, index) => (
                  <Box
                    key={owner.employeeDataId || index}
                    onClick={() => toggleOwner(owner.employeeDataId)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      cursor: "pointer",
                      borderBottom:
                        index < owners.length - 1
                          ? `1px solid ${tc.border.subtle}`
                          : "none",
                      "&:hover": { backgroundColor: tc.background.hover },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={selected.has(owner.employeeDataId)}
                      sx={{ p: 0.5 }}
                    />
                    <Avatar
                      sx={{
                        width: 26,
                        height: 26,
                        fontSize: "11px",
                        fontWeight: 600,
                        bgcolor: alpha(info.main, 0.14),
                        color: info.text,
                      }}
                    >
                      {initials(owner.name || owner.email)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "13.5px",
                          fontWeight: 500,
                          color: tc.text.strong,
                        }}
                      >
                        {owner.name || owner.email}
                      </Typography>
                      {owner.integrations && (
                        <Typography
                          sx={{ fontSize: "11.5px", color: tc.text.disabled }}
                        >
                          {owner.integrations}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        }
      />

      <RequestReviewDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        checkId={checkId}
      />

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default AIEvidenceLockedStrip;
